import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Rodal from 'rodal';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import { userAuth } from '../../../contexts/userContext';
import './Login.css';
import Icon from '../../../components/icon/Icon';
// include styles
import 'rodal/lib/rodal.css';
import { Ralert } from '../../common/vhelper/Normarodallalert';
import Loader from '../../common/vhelper/Loader';
import { useTranslation } from 'react-i18next';
import LANG, { getLangWithKey } from '../../../lang';
import { HeaderRight } from '../../../layout/Header/Header';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import showNotification from '../../../components/extras/showNotification';
import Popovers from '../../../components/bootstrap/Popovers';
import apiService from '../../../api/apiService';
import { getConfig } from '../../../config';
import { useDueDate } from '../../../contexts/dueDateContext';
import getClientSlug from '../../../helpers/clientSlug.js';
import { buildJsonHeaders } from '../../../helpers/apiHeaders';
import { useLoginSettings } from '../../../contexts/loginSettingsContext';

const LoginHeader = ({ isNewUser }) => {
	const { t } = useTranslation();
	if (isNewUser) {
		return (
			<>
				<div className='text-center h3 fw-bold mt-2'>{t('Create Account,')}</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center h1 fw-bold mt-3'>{t('Welcome,')}</div>
			<div className='text-center h4 text-muted mb-5'>{t('Sign in to continue!')}</div>
		</>
	);
};

const Login = ({ isSignUp }) => {
	const { t } = useTranslation();
	const { i18n } = useTranslation();

	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	const [isNewUser] = useState(isSignUp);

	const [searchParams] = useSearchParams();
	const isDemoMode = searchParams.get('demo') === 'true';

	const emailRef = useRef();
	const usrnamelRef = useRef();
	const passwordRef = useRef();

	const [isempty2, setIsempty2] = useState(false);
	const [loadalert, setLoadalert] = useState(false);
	const [msgloadalert, setMsgLoadalert] = useState('');
	const [msgtypeloadalert, setMsgtypeLoadalert] = useState('');

	const [loaderv, setLoaderv] = useState(true);

	const navigate = useNavigate();

	const lemailRef = useRef();
	const lpasswordRef = useRef();

		const { loginSettings, loginSettingsLang, loading: loginSettingsLoading } = useLoginSettings();

	const loginBgFromSettings = loginSettings?.background_image;
	const defaultLoginBg = `${process.env.PUBLIC_URL}/assets/img/abstract/Main_a.jpg`;
	const loginBg =
		loginBgFromSettings && String(loginBgFromSettings).trim()
			? loginBgFromSettings
			: defaultLoginBg;


	// Set demo credentials if in demo mode

	useEffect(() => {
		if (!loaderv && isDemoMode) {
			const timer = setTimeout(() => {
				if (lemailRef.current && lpasswordRef.current) {
					lemailRef.current.value = 'business@interflext.com';
					lpasswordRef.current.value = 'Demo123@';
				}
			}, 200);

			return () => clearTimeout(timer);
		}
	}, [isDemoMode, loaderv]);

	const { token, setToken } = useContext(userAuth);

	// reg pw show
	const [showPassword, setShowPassword] = useState(false);
	const [isemptyp, setIsemptyp] = useState({ error: false, msg: t('*Required') });
	const [isemptym, setIsemptym] = useState({ error: false, msg: t('*Required') });

	const [isLoading, setIsLoading] = useState(false);

	const styledBtn = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const { dueDateStatus } = useDueDate();
	const [isTab, setIsTab] = useState(false);
	const [isCompact, setIsCompact] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsTab(window.innerWidth > 782 && window.innerWidth <= 1230);
			setIsCompact(window.innerWidth < 992);
		};

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	let warningMessage = '';
	let warningTitle = '';

	if (dueDateStatus === 'INVALID') {
		warningTitle = 'Payment overdue';
		warningMessage =
			'Your payment is now overdue. Kindly pay within 10 days to avoid service suspension.';
	} else if (dueDateStatus === 'WARNING') {
		warningTitle = 'Payment due soon';
		warningMessage =
			'Please ensure your payment is made by the due date to avoid any service interruptions.';
	}

	const changeLanguage = (lng) => {
		i18n.changeLanguage(lng);
		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
				<span>{`Language changed to ${getLangWithKey(lng)?.text}`}</span>
			</span>,
		);
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	useEffect(() => {
		const currentLang = getLangWithKey(i18n.language);

		if (!currentLang) {
			i18n.changeLanguage(LANG.EN.lng);
		}
	}, []);

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	useEffect(() => {
		localStorage.setItem('theme', darkModeStatus ? 'dark' : 'light');
		document.documentElement.setAttribute('theme', darkModeStatus ? 'dark' : 'light');
	}, [darkModeStatus]);

	const login = async () => {
		if (isLoading) return;
		let cansendapi = true;
		const emailRegex = /^(?!\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;
		// const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (!lemailRef.current.value) {
			setIsemptym({ error: true, msg: t('*Required') });
			cansendapi = false;
		} else if (emailRegex.test(`${lemailRef.current.value}`.trim())) {
			setIsemptym({ error: false });
		} else {
			setIsemptym({ error: true, msg: t('Enter an email address with a valid format.') });
			cansendapi = false;
		}
		if (!lpasswordRef.current.value) {
			setIsemptyp({ error: true, msg: t('*Required') });
			cansendapi = false;
		} else {
			setIsemptyp({ error: false });
		}

		if (cansendapi) {
			setIsLoading(true);
			const item = {
				action: 'cuslog',
				email: lemailRef.current.value,
				password: lpasswordRef.current.value,
			};
			const weblink = getConfig().REACT_APP_WEB_LINK;
			const slug = getClientSlug();
			const payload = slug ? { ...item, slug } : item;
			const headers = await buildJsonHeaders();

			try {
				const res = await fetch(weblink, {
					method: 'POST',
					headers,
					body: JSON.stringify(payload),
				});
				const result = await res.json();

				if (result.type === 'Success' || result.type === 'success') {
					if (result.data.userrole === 'vender') {
						setToken(result.data);
						localStorage.setItem('user-auth', JSON.stringify(result.data));
						navigate('dashboard');
						fetchBranches(result.data);
					} else {
						startmodel();
						setMsgLoadalert(result.message);
						setMsgtypeLoadalert(result.type);
					}
				} else {
					startmodel();
					setMsgLoadalert(result.message);
					setMsgtypeLoadalert(result.type);
				}
			} catch (error) {
				/*startmodel();
				setMsgLoadalert(result.message);
				setMsgtypeLoadalert(result.type);*/
				console.error('Error:', error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	const fetchBranches = async (tokenData) => {
		const item = { action: 'get_branches_list' };

		try {
			const flechdata = await apiService(item, tokenData, setToken, navigate);

			if (Array.isArray(flechdata.data)) {
				const temp = flechdata.data
					.filter((ele) => ele.branch_name !== 'All')
					.map((ele) => ({
						value: ele.branch_id,
						text: ele.branch_name,
						is_default: ele.is_default,
					}));

				localStorage.setItem('branches-list', JSON.stringify(temp));
			} else {
				localStorage.removeItem('branches-list');
			}
		} catch (error) {
			localStorage.removeItem('branches-list');
			console.error('Error fetching branches:', error);
		}
	};

	const closemodel = () => {
		setLoadalert(false);
	};
	const startmodel = () => {
		setLoadalert(true);
	};
	const closemodel2 = () => {
		setIsempty2(false);
	};
	const startmodel2 = () => {
		setIsempty2(true);
	};
	useEffect(() => {
		setLoaderv(true);
		const authtimer = setTimeout(() => {
			if (token.email) {
				navigate('/dashboard');
			} else {
				setLoaderv(false);
			}
		}, 300);
		return () => {
			clearTimeout(authtimer);
		};
	}, [token.email]);
	if (loaderv) {
		return <Loader />;
	}

	return (
		<>
			<Ralert
				msgloadalert={msgloadalert}
				msgtypeloadalert={msgtypeloadalert}
				loadalert={loadalert}
				closemodel={closemodel}
			/>

			<div className='bg-img' style={{ backgroundImage: `url(${loginBg})` }}>

				<PageWrapper title={isNewUser ? t('Sign Up') : t('Login')}>
					{/* <div
						className='g-3'
						style={{
							position: 'absolute',
							top: 8,
							left: 8,
							display: 'flex',
							justifyContent: 'flex-end',
						}}>
						<div className='col-md-auto'>
							{(dueDateStatus === 'INVALID' || dueDateStatus === 'WARNING') && (
								<div>
									<div
										style={{
											backgroundColor:
												dueDateStatus === 'INVALID' ? '#f8d7da' : '#fff3cd',
											color:
												dueDateStatus === 'INVALID' ? '#721c24' : '#856404',
											padding: '5px',
											borderRadius: '5px',
											border: `1px solid ${
												dueDateStatus === 'INVALID' ? '#f5c6cb' : '#ffeeba'
											}`,
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-start',
											gap: '2px',
											fontSize: '11px',
											lineHeight: '1.4',
											marginTop: isCompact ? '20px' : '0px',
										}}>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '6px',
											}}>
											<Icon icon='info' className='btn-icon' />
											<strong style={{ fontSize: '13px' }}>
												{warningTitle}
											</strong>
										</div>
										<span>{warningMessage}</span>
									</div>
								</div>
							)}
						</div>
					</div> */}
					<div
						className='g-3'
						style={{
							position: 'absolute',
							top: 8,
							right: 8,
							display: 'flex',
							justifyContent: 'flex-end',
						}}>
						<div className='col-md-auto'>
							<div className='row g-3'>
								<div className='col-auto'>
									<Popovers trigger='hover' desc='Dark / Light mode'>
										<Button
											{...styledBtn}
											icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
											onClick={() => setDarkModeStatus((prev) => !prev)}
											aria-label='Toggle fullscreen'
											data-tour='dark-mode'
										/>
									</Popovers>
								</div>
								<div className='col-auto'>
									<Dropdown>
										<DropdownToggle hasIcon={false}>
											<Button
												{...styledBtn}
												icon={getLangWithKey(i18n.language)?.icon}
												aria-label='Change language'
												data-tour='lang-selector'
											/>
										</DropdownToggle>
										<DropdownMenu isAlignmentEnd data-tour='lang-selector-menu'>
											{Object.keys(LANG).map((i) => (
												<DropdownItem key={LANG[i].lng}>
													<Button
														icon={LANG[i].icon}
														onClick={() => changeLanguage(LANG[i].lng)}>
														{LANG[i].text}
													</Button>
												</DropdownItem>
											))}
										</DropdownMenu>
									</Dropdown>
								</div>
							</div>
						</div>
					</div>
					<Page
						className='p-0 mt-3'
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							minHeight: '100vh',
						}}>
						<Card
							className='shadow-3d-dark'
							data-tour='login-page'
							style={{
								maxWidth: '400px',
								maxHeight: isDemoMode ? '580px' : '545px',
							}}>
							<CardBody>
								<div className='text-center my-3'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}>
										<Logo width={200} />
									</Link>
								</div>
								<div
									className={classNames('rounded-3', {
										'bg-l10-dark': !darkModeStatus,
										'bg-lo10-dark': darkModeStatus,
									})}>
									<div className=''>
										<div className='col'>
											{/* <Button
													color={darkModeStatus ? 'light' : 'dark'}
													isLight={!!isNewUser}
													className='rounded-1 w-100'
													size='lg'
													onClick={() => {
														setUsernameInput(false);
														setIsNewUser(!isNewUser);
													}}>
													Login
												</Button> */}
										</div>
										{/* <div className='col'>
												<Button
													color={darkModeStatus ? 'light' : 'dark'}
													isLight={!isNewUser}
													className='rounded-1 w-100'
													size='lg'
													onClick={() => {
														setUsernameInput(false);
														setIsNewUser(!isNewUser);
													}}>
													Sign Up
												</Button>
											</div> */}
									</div>
								</div>

								<LoginHeader isNewUser={isNewUser} />

								<form className='row g-4'>
									{isNewUser ? (
										<div className='mt-5'>
											<div className='col-12'>
												<FormGroup isFloating label={t('Your email')}>
													<Input type='email' ref={emailRef} />
												</FormGroup>
											</div>
											<div className='col-12 mt-4'>
												<FormGroup isFloating label={t('Your name')}>
													<Input type='text' ref={usrnamelRef} />
												</FormGroup>
											</div>
											<div className='col-12 mt-3'>
												<FormGroup isFloating label={t('Password')}>
													<Input type='password' ref={passwordRef} />
												</FormGroup>
											</div>
											<div className='col-12 mt-3'>
												<Button
													color='info'
													className='w-100 py-3'
													// onClick={() => signUpp()}
												>
													{t('Sign Up')}
												</Button>
											</div>
										</div>
									) : (
										<>
											<div className='col-12 mt-4'>
												{isDemoMode && (
													<div
														className={classNames('mb-3 p-3', {
															'bg-light border border-success':
																!darkModeStatus,
															'bg-lo10-dark border border-success':
																darkModeStatus,
														})}
														style={{
															borderLeftWidth: '4px',
															borderRadius: '6px',
															fontSize: '12px',
														}}>
														<div
															className='d-flex align-items-center mb-2'
															style={{ fontWeight: 600 }}>
															<Icon
																icon='Info'
																size='lg'
																className='me-2'
																color='success'
															/>
															Demo Login
														</div>

														<div
															style={{
																lineHeight: 1.6,
																opacity: 0.85,
															}}>
															<div>
																<strong>Email:</strong>{' '}
																employee@interflext.com
															</div>
															<div>
																<strong>Password:</strong> Demo123@
															</div>
														</div>
													</div>
												)}
												<FormGroup
													id='lemail'
													isFloating
													label={t('Your Email or Username')}>
													<Input autoComplete='email' ref={lemailRef} />
												</FormGroup>
												{isemptym.error ? (
													<p
														style={{
															color: 'red',
															fontSize: '12px',
														}}>
														{' '}
														{isemptym.msg
															? isemptym.msg
															: t('*Required')}
													</p>
												) : (
													<br />
												)}

												<FormGroup isFloating label={t('Password')}>
													<Input
														type={showPassword ? 'text' : 'password'}
														autoComplete='password'
														ref={lpasswordRef}
													/>
													<button
														type='button'
														onClick={togglePasswordVisibility}
														style={{
															position: 'absolute',
															right: '10px', // Adjust the right position as needed
															top: '50%',
															transform: 'translateY(-50%)',
															border: 'none',
															background: 'none',
															cursor: 'pointer',
														}}>
														{showPassword ? (
															<Icon
																style={{
																	color: darkModeStatus
																		? '#c9c3c3'
																		: 'black',
																}}
																icon='Visibility'
																size='2x'
															/>
														) : (
															<Icon
																style={{
																	color: darkModeStatus
																		? '#c9c3c3'
																		: 'black',
																}}
																icon='VisibilityOff'
																size='2x'
															/>
														)}{' '}
													</button>
												</FormGroup>
												{isemptyp.error ? (
													<p
														style={{
															color: 'red',
															fontSize: '12px',
														}}>
														{' '}
														{isemptyp.msg
															? isemptyp.msg
															: t('*Required')}
													</p>
												) : (
													<br />
												)}
												<a
													href='landing/forgot'
													className={classNames(
														'text-decoration-none me-3',
														{
															'link-light': darkModeStatus,
															white: !darkModeStatus,
														},
													)}
													style={{
														marginLeft: '8px',
														fontSize: 13,
													}}>
													{t('Forgotten password?')}
												</a>
											</div>
											<div className='col-12 mb-5'>
												<Button
													color='success'
													className='w-100 py-3'
													onClick={() => login()}
													disabled={isLoading}>
													{t('Login')}
												</Button>
											</div>
										</>
									)}
								</form>
							</CardBody>
						</Card>
						{/* <div
									className=''
									style={{ 
										// marginLeft: '240px', marginTop: '8px' 
										display:'flex',
											flexDirection:'row',
											alignItems:'center'
										}}>
									<a
										href='landing/privacy'
										className={classNames('text-decoration-none me-3', {
											'link-light': isNewUser,
											'link-dark': !isNewUser,
										})}
										style={{ color: 'white' }}>
										Privacy policy
									</a>
									<a
										href='landing/terms'
										className={classNames('link-light text-decoration-none', {
											'link-light': isNewUser,
											'link-dark': !isNewUser,
										})}
										style={{ color: 'white' }}>
										Terms of use
									</a>
								</div> */}

						{/* </div>
						</div> */}
					</Page>
				</PageWrapper>
			</div>
		</>
	);
};
Login.propTypes = {
	isSignUp: PropTypes.bool,
};
Login.defaultProps = {
	isSignUp: false,
};

export default Login;
